import { ChevronDown } from "lucide-react";
import { useId, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { FaqClusterLoadingShell } from "@/components/FaqClusterLoadingShell";
import { FaqSearchControl } from "@/components/FaqSearchControl";
import { filterFaqItems } from "@/lib/faqSearch";

export type FaqAccordionItem =
  | { question: string; answer: ReactNode }
  | { q: string; a: ReactNode };

type FaqAccordionProps = {
  items: readonly FaqAccordionItem[];
  headingLevel?: 2 | 3 | 4;
  defaultOpenIndex?: number | null;
  openIndex?: number | null;
  onOpenIndexChange?: (index: number | null) => void;
  onItemToggle?: (index: number, isOpen: boolean) => void;
  className?: string;
  itemClassName?: string;
  buttonClassName?: string;
  questionClassName?: string;
  answerClassName?: string;
  answerTextClassName?: string;
  iconClassName?: string;
  iconOpenClassName?: string;
  icon?: "chevron" | "plus";
  containerStyle?: CSSProperties;
  itemStyle?: CSSProperties;
  buttonStyle?: CSSProperties;
  questionStyle?: CSSProperties;
  answerStyle?: CSSProperties;
  answerTextStyle?: CSSProperties;
  iconStyle?: CSSProperties;
  renderAnswer?: (item: FaqAccordionItem, index: number) => ReactNode;
};

function getQuestion(item: FaqAccordionItem) {
  return "question" in item ? item.question : item.q;
}

function getAnswer(item: FaqAccordionItem) {
  return "answer" in item ? item.answer : item.a;
}

/**
 * A single FAQ source can feed the visible H3 > button pattern and FAQPage
 * JSON-LD. Answers stay in the DOM while closed, using `hidden` only for
 * presentation, so crawlers and assistive technology can rely on stable markup.
 */
export default function FaqAccordion({
  items,
  headingLevel = 3,
  defaultOpenIndex = null,
  openIndex,
  onOpenIndexChange,
  onItemToggle,
  className,
  itemClassName,
  buttonClassName,
  questionClassName,
  answerClassName,
  answerTextClassName,
  iconClassName = "h-5 w-5 shrink-0 transition-transform duration-200",
  iconOpenClassName = "rotate-180",
  icon = "chevron",
  containerStyle,
  itemStyle,
  buttonStyle,
  questionStyle,
  answerStyle,
  answerTextStyle,
  iconStyle,
  renderAnswer,
}: FaqAccordionProps) {
  const [uncontrolledOpenIndex, setUncontrolledOpenIndex] = useState<number | null>(defaultOpenIndex);
  const [faqQuery, setFaqQuery] = useState("");
  const isControlled = openIndex !== undefined;
  const activeIndex = isControlled ? openIndex : uncontrolledOpenIndex;
  const idPrefix = useId().replace(/:/g, "");
  const Heading = `h${headingLevel}` as "h2" | "h3" | "h4";
  const shouldEnableSearch = items.length >= 6;
  const filteredItems = useMemo(
    () => filterFaqItems(items, faqQuery, getQuestion, getAnswer),
    [items, faqQuery],
  );

  const toggle = (index: number) => {
    const willOpen = activeIndex !== index;
    const nextIndex = willOpen ? index : null;

    if (!isControlled) setUncontrolledOpenIndex(nextIndex);
    onOpenIndexChange?.(nextIndex);
    onItemToggle?.(index, willOpen);
  };

  return (
    <FaqClusterLoadingShell itemCount={items.length}>
      <div className={className} style={containerStyle}>
        {shouldEnableSearch ? (
          <FaqSearchControl
            query={faqQuery}
            onQueryChange={setFaqQuery}
            totalCount={items.length}
            resultCount={filteredItems.length}
            resultsId={`${idPrefix}-faq-search`}
          />
        ) : null}
        <div id={`${idPrefix}-faq-search-results`}>
          {filteredItems.map(({ item, index }) => {
            const question = getQuestion(item);
            const isOpen = activeIndex === index;
            const questionId = `${idPrefix}-faq-q-${index + 1}`;
            const answerId = `${idPrefix}-faq-a-${index + 1}`;

            return (
              <div key={question} className={itemClassName} style={itemStyle}>
                <Heading className={questionClassName ?? "m-0"} style={questionStyle}>
                  <button
                    type="button"
                    id={questionId}
                    aria-expanded={isOpen}
                    aria-controls={answerId}
                    className={buttonClassName}
                    style={buttonStyle}
                    onClick={() => toggle(index)}
                  >
                    <span>{question}</span>
                    {icon === "plus" ? (
                      <span
                        aria-hidden="true"
                        className={`${iconClassName} ${isOpen ? iconOpenClassName : ""}`.trim()}
                        style={iconStyle}
                      >
                        +
                      </span>
                    ) : (
                      <ChevronDown
                        aria-hidden="true"
                        className={`${iconClassName} ${isOpen ? iconOpenClassName : ""}`.trim()}
                        style={iconStyle}
                      />
                    )}
                  </button>
                </Heading>
                <div
                  id={answerId}
                  role="region"
                  aria-labelledby={questionId}
                  hidden={!isOpen}
                  className={answerClassName}
                  style={answerStyle}
                >
                  {renderAnswer ? renderAnswer(item, index) : <p className={answerTextClassName} style={answerTextStyle}>{getAnswer(item)}</p>}
                </div>
              </div>
            );
          })}
          {shouldEnableSearch && filteredItems.length === 0 ? (
            <p className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
              No FAQ answer matches that search. Try a different phrase or clear the search.
            </p>
          ) : null}
        </div>
      </div>
    </FaqClusterLoadingShell>
  );
}
